import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * OAuth2 Callback Page
 * Handles the redirect from Google OAuth after user grants permissions
 */
export const OAuth2Callback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the authorization code from URL parameters
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        // Handle user denial or errors from Google
        if (error) {
          console.error('❌ OAuth error:', error);
          setStatus('error');
          setErrorMessage(error === 'access_denied'
            ? 'You denied access to Google Drive. Please try again and grant permissions.'
            : `OAuth error: ${error}`);

          // Close popup after 3 seconds
          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        // Validate that we have the code
        if (!code) {
          console.error('❌ No authorization code received');
          setStatus('error');
          setErrorMessage('No authorization code received from Google');

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        console.log('✅ Received authorization code from Google:', code);

        // Show success message
        setStatus('success');

        // Check if running on localhost
        const isLocalhost = window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';

        if (isLocalhost) {
          // On localhost: Store code to display it and close after 3 seconds
          setErrorMessage(code);
          setTimeout(() => {
            console.log('🔄 Closing popup with code');
            window.close();
          }, 3000);
        } else {
          // On production: Close immediately after getting the code
          setTimeout(() => {
            console.log('🔄 Closing popup with code');
            window.close();
          }, 500);
        }

      } catch (error: any) {
        console.error('❌ Error during OAuth callback:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to complete authentication');

        // Close popup after 3 seconds
        setTimeout(() => {
          window.close();
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Connecting to Google Drive...
            </h2>
            <p className="text-gray-600">
              Please wait while we complete the authentication.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Successfully Connected!
            </h2>
            {errorMessage ? (
              // Localhost: Show the authorization code
              <>
                <p className="text-gray-600 mb-4">
                  Authorization successful. Here is your code:
                </p>
                <div className="bg-gray-100 rounded-lg p-4 mb-4">
                  <p className="text-sm font-mono text-gray-800 break-all select-all">
                    {errorMessage}
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  Window will close automatically in 3 seconds...
                </p>
              </>
            ) : (
              // Production: Simple redirect message
              <p className="text-gray-600">
                Redirecting you back to Ideoz...
              </p>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Connection Failed
            </h2>
            <p className="text-gray-600 mb-4">
              {errorMessage}
            </p>
            <p className="text-sm text-gray-500">
              Redirecting back to Ideoz...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuth2Callback;
